/**
 * Widget affichant le poids en temps réel de la balance
 * Affiche l'état de connexion et permet les actions (connect, tare)
 * Version améliorée avec détection de stabilité et meilleur feedback
 */

import { useScale } from '../../hooks/useScale';
import { usePOSStore } from '../../hooks/usePOSStore';
import { useVendorSession } from '../../contexts/VendorSessionContext';
import { useState, useEffect, useRef } from 'react';
import { Scale, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

// Tares prédéfinies (en grammes)
const PRESET_TARES = {
  FILET_AVEC_FICELLE: { label: 'Filet avec ficelle', weight: 36 },
  FILET_SANS_FICELLE: { label: 'Filet sans ficelle', weight: 32 },
};

// Stabilité balance — tolérance aux petites fluctuations (vent au marché).
// Une variation ≤ tolérance ne casse PAS la fenêtre de stabilité en cours : sans elle,
// le moindre flottement au gramme près repousse « stable » indéfiniment et chaque
// encodage de produit au poids attend le timeout de 3 s de getStableWeight().
// Réglable PAR TERMINAL sans redéploiement (console Safari) :
//   localStorage.setItem('scale_stability_tolerance_g', '12')   // marché venteux
//   localStorage.setItem('scale_stability_window_ms', '600')
/** Au-delà, la balance ne parle plus : son dernier poids n'est plus fiable. */
const SILENCE_BALANCE_MS = 6000;

const SCALE_STABILITY_TOLERANCE_G = 6;
const SCALE_STABILITY_WINDOW_MS = 800;

function readNumberSetting(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  } catch {
    return fallback;
  }
}

export default function ScaleWidget() {
  const {
    connected,
    connecting,
    error,
    currentWeight,
    adapterName,
    isManualMode,
    canAutoConnect,
    connect,
    disconnect,
    setManualWeight,
  } = useScale();

  const { setCurrentScaleWeight, setScaleStatus, scaleStatus, activeTareGrams, setActiveTareGrams, resetTare } = usePOSStore();
  const { currentVendor } = useVendorSession();

  // Auto-connexion de la balance dès qu'un vendeur est actif (login OU changement de vendeur),
  // pour supprimer les 2 clics manuels. Une seule tentative par vendeur (pas de boucle si le
  // bridge est down) ; le bouton manuel reste disponible en secours. Ignoré pour Web Serial
  // (Chrome) qui exige un geste utilisateur — non concerné par les Mac mini Safari.
  const autoConnectVendorRef = useRef<string | null>(null);
  useEffect(() => {
    const vid = currentVendor?.id ?? null;
    if (!vid) {
      autoConnectVendorRef.current = null;
      return;
    }
    if (canAutoConnect && !connected && !connecting && autoConnectVendorRef.current !== vid) {
      autoConnectVendorRef.current = vid;
      logger.log('[ScaleWidget] 🔌 Auto-connexion balance pour le vendeur', vid);
      connect().catch((err) => logger.error('[ScaleWidget] Auto-connexion échouée:', err));
    }
  }, [currentVendor, canAutoConnect, connected, connecting, connect]);

  const [manualInput, setManualInput] = useState('');
  const [manualTareInput, setManualTareInput] = useState('');

  const stableTimerRef = useRef<NodeJS.Timeout>();
  // Poids de référence de la fenêtre de stabilité en cours (tolérance vent)
  const stableAnchorRef = useRef<number | null>(null);
  /** Horodatage du dernier relevé reçu de la balance (détection du silence). */
  const dernierRelevéRef = useRef<number>(Date.now());
  const [balanceMuette, setBalanceMuette] = useState(false);

  // Poids net en grammes (peut être négatif si contenant retiré après tare)
  const netWeight = currentWeight ? currentWeight.weight - activeTareGrams : 0;

  // Synchroniser l'état de connexion avec le store (indépendamment du poids)
  useEffect(() => {
    logger.log('[ScaleWidget] 🔌 Connection status changed:', connected);
    if (connected) {
      setScaleStatus('reading', true);
      logger.log('[ScaleWidget] ✅ Balance connectée - store updated with isScaleConnected: true');
    } else {
      setScaleStatus('disconnected', false);
      logger.log('[ScaleWidget] ❌ Balance déconnectée - store updated with isScaleConnected: false');
    }
  }, [connected, setScaleStatus]);

  // Synchroniser le poids avec le store
  useEffect(() => {
    if (!connected) {
      // Si pas connecté : purger la fenêtre de stabilité en cours
      if (stableTimerRef.current) {
        clearTimeout(stableTimerRef.current);
      }
      stableAnchorRef.current = null;
      return;
    }

    logger.log('[ScaleWidget] 📊 Weight update:', {
      netWeight,
      currentWeight: currentWeight?.weight,
      activeTareGrams,
      stable: currentWeight?.stable
    });

    setCurrentScaleWeight(Math.max(0, netWeight));

    // Gestion de la stabilité du poids, avec tolérance aux flottements (vent) :
    // seule une variation > tolérance (article posé/retiré) réarme la fenêtre.
    const toleranceG = readNumberSetting('scale_stability_tolerance_g', SCALE_STABILITY_TOLERANCE_G);
    const windowMs = readNumberSetting('scale_stability_window_ms', SCALE_STABILITY_WINDOW_MS);

    const anchor = stableAnchorRef.current;
    const withinBand = anchor !== null && Math.abs(netWeight - anchor) <= toleranceG;

    if (!withinBand) {
      stableAnchorRef.current = netWeight;
      setScaleStatus('reading', true);
      logger.log('[ScaleWidget] 📖 Weight changed - status: reading');

      if (stableTimerRef.current) {
        clearTimeout(stableTimerRef.current);
      }
      stableTimerRef.current = setTimeout(() => {
        setScaleStatus('stable', true);
        logger.log('[ScaleWidget] ✅ Poids stable (±' + toleranceG + ' g) après', windowMs, 'ms:', netWeight, 'g');
      }, windowMs);
    }
    // withinBand : flottement dans la tolérance — la fenêtre en cours continue de courir

    // Le flux vient de parler : on note l'instant, pour détecter le silence.
    dernierRelevéRef.current = Date.now();
  }, [netWeight, connected, setCurrentScaleWeight, setScaleStatus]);

  /**
   * Balance muette : le dernier poids ne doit pas rester marqué « Stable ».
   *
   * Le port série reste ouvert quand la balance s'éteint — batterie à plat au
   * marché, mise en veille de l'appareil : le convertisseur USB reste alimenté
   * par le Mac. Aucune erreur ne remonte, l'effet ci-dessus ne se rejoue pas,
   * et le dernier poids reçu restait affiché en vert, prêt à partir au panier.
   * Le vendeur pesait l'article suivant et encaissait le poids du précédent.
   *
   * On surveille donc le SILENCE : plus de relevé depuis six secondes (la
   * balance émet en continu), et le poids cesse d'être réputé stable.
   */
  useEffect(() => {
    if (!connected) return;
    const veille = setInterval(() => {
      const silence = Date.now() - dernierRelevéRef.current;
      if (silence > SILENCE_BALANCE_MS) {
        setBalanceMuette(true);
        setScaleStatus('reading', true); // plus jamais « stable » sur un poids figé
        stableAnchorRef.current = null;
        if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
      } else {
        setBalanceMuette(false);
      }
    }, 2000);
    return () => clearInterval(veille);
  }, [connected, setScaleStatus]);

  // Nettoyage du timer de stabilité au démontage uniquement (un cleanup à chaque
  // re-render tuait la fenêtre au moindre flottement de poids)
  useEffect(() => () => {
    if (stableTimerRef.current) {
      clearTimeout(stableTimerRef.current);
    }
  }, []);

  const handleConnect = async () => {
    try {
      await connect();
    } catch (err) {
      logger.error('Erreur connexion balance:', err);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
    } catch (err) {
      logger.error('Erreur déconnexion balance:', err);
    }
  };

  const handleManualSubmit = () => {
    if (!setManualWeight) return;

    const weight = parseFloat(manualInput);
    if (isNaN(weight) || weight < 0) {
      toast.error('Poids invalide');
      return;
    }

    // Convertir en grammes (input en kg)
    setManualWeight(weight * 1000);
    setManualInput('');
  };

  // Handlers pour la tare
  const handleSetManualTare = () => {
    const tareGrams = parseFloat(manualTareInput);
    if (isNaN(tareGrams) || tareGrams < 0) {
      toast.error('Tare invalide');
      return;
    }
    setActiveTareGrams(tareGrams);
    setManualTareInput('');
  };

  const handleSetPresetTare = (tareGrams: number) => {
    setActiveTareGrams(tareGrams);
  };

  const handleResetTare = () => {
    resetTare();
  };

  // Tarer depuis le poids actuel de la balance (contenant posé ou remise à zéro)
  const handleTareFromScale = () => {
    if (!currentWeight) {
      toast.error('Balance non disponible');
      return;
    }
    const grossWeight = Math.max(0, Math.round(currentWeight.weight));
    if (grossWeight === 0) {
      resetTare();
      toast.success('Tare réinitialisée');
    } else {
      setActiveTareGrams(grossWeight);
      toast.success(`Tare: ${grossWeight}g`);
    }
  };

  // Formatage du poids brut
  const formatGrossWeight = () => {
    if (!currentWeight) return '---';
    const kg = (currentWeight.weight / 1000).toFixed(3);
    const stableIcon = currentWeight.stable ? '✓' : '~';
    return `${stableIcon} ${kg} kg`;
  };

  // Formatage du poids net
  const formatNetWeight = () => {
    if (!currentWeight) return '---';
    const kg = (netWeight / 1000).toFixed(3);
    const stableIcon = currentWeight?.stable ? '✓' : '~';
    return `${stableIcon} ${kg} kg`;
  };

  // Indicateur de statut visuel
  const getStatusColor = () => {
    if (!connected) return 'bg-red-500';
    if (scaleStatus === 'reading') return 'bg-yellow-500';
    if (scaleStatus === 'stable') return 'bg-green-500';
    return 'bg-gray-500';
  };

  // Mode compact si deconnecte (pas de balance branchee)
  if (!connected && !connecting) {
    return (
      <button
        onClick={handleConnect}
        className="w-full bg-white rounded-xl border-2 border-gray-200 shadow-sm px-4 py-3 flex items-center justify-center gap-3 active:bg-gray-50 transition-colors"
      >
        <span className="text-xl">🔌</span>
        <span className="text-sm font-medium text-gray-600">Connecter la balance</span>
        <span className="text-xs text-gray-400">({adapterName})</span>
      </button>
    );
  }

  // Mode compact si en cours de connexion
  if (connecting) {
    return (
      <div className="w-full bg-white rounded-xl border-2 border-gray-200 shadow-sm px-4 py-3 flex items-center justify-center gap-3">
        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="text-sm font-medium text-gray-600">Connexion a la balance...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm flex flex-row gap-4 p-4 min-h-[200px]">
      {/* Section 1: Poids NET (principal) */}
      <div className="flex-1 bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200 flex flex-col justify-center">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale className="h-6 w-6 text-green-700" />
            <span className="text-base font-semibold text-green-700">Poids NET</span>
            {scaleStatus === 'stable' && netWeight > 0 && (
              <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded font-medium">Stable</span>
            )}
            {/* La balance ne répond plus : le poids affiché est le DERNIER reçu,
                il ne correspond peut-être plus à ce qui est sur le plateau. */}
            {balanceMuette && (
              <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded font-bold">
                ⚠️ Balance muette — poids figé
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {connected ? (
              <Wifi className="h-5 w-5 text-green-500" />
            ) : (
              <WifiOff className="h-5 w-5 text-red-400" />
            )}
            <div className={`w-2.5 h-2.5 rounded-full ${getStatusColor()} ${connected ? 'animate-pulse' : ''}`} />
          </div>
        </div>
        <div className={`text-6xl font-bold font-mono text-center mt-2 ${netWeight < 0 ? 'text-red-600' : 'text-green-900'}`}>
          {formatNetWeight()}
        </div>
        {activeTareGrams > 0 && (
          <div className="text-sm text-green-700 text-center mt-2 font-medium">
            Brut: {formatGrossWeight()} · Tare: {activeTareGrams}g
          </div>
        )}
        {/* N'afficher l'erreur QUE si la balance est réellement déconnectée. Un message
            périmé (drop transitoire déjà reconnecté par Socket.IO) ne doit plus s'afficher
            quand `connected` est revenu à true. */}
        {error && !connected && (
          <div className="text-sm text-red-600 text-center mt-2">⚠️ {error}</div>
        )}
      </div>

      {/* Section 2: Tares prédéfinies (si connecté et pas mode manuel) */}
      {connected && !isManualMode && (
        <div className="w-72 bg-orange-50 rounded-xl p-4 border-2 border-orange-200 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-orange-900">Tare</span>
            {activeTareGrams > 0 && (
              <button
                onClick={handleResetTare}
                className="text-sm px-3 py-1.5 text-orange-700 hover:bg-orange-100 rounded-lg font-semibold transition-colors min-h-[36px]"
              >
                ✖ RAZ
              </button>
            )}
          </div>
          {/* Bouton TARER depuis la balance */}
          <button
            onClick={handleTareFromScale}
            className="w-full px-3 py-3 text-lg font-bold bg-orange-600 text-white rounded-lg hover:bg-orange-700 active:bg-orange-800 transition-colors min-h-[52px]"
          >
            TARER
          </button>
          {/* Presets de tare */}
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(PRESET_TARES).map(([key, { weight }]) => (
              <button
                key={key}
                onClick={() => handleSetPresetTare(weight)}
                className={`px-3 py-3 text-base font-semibold rounded-lg border-2 transition-colors min-h-[48px] ${
                  activeTareGrams === weight
                    ? 'bg-orange-500 text-white border-orange-600'
                    : 'bg-white text-orange-800 border-orange-300 hover:bg-orange-100'
                }`}
              >
                {weight}g
              </button>
            ))}
          </div>
          {/* Tare manuelle */}
          <div className="flex gap-2">
            <input
              type="number"
              step="1"
              min="0"
              value={manualTareInput}
              onChange={(e) => setManualTareInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSetManualTare()}
              placeholder="g"
              className="flex-1 px-3 py-2 text-base border-2 border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 min-h-[44px]"
            />
            <button
              onClick={handleSetManualTare}
              disabled={!manualTareInput}
              className="px-4 py-2 text-base font-semibold bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-40 min-h-[44px]"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Section 3: Mode manuel (si connecté en mode manuel) */}
      {isManualMode && connected && (
        <div className="w-56 bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200 flex flex-col justify-center gap-2">
          <label className="text-base font-semibold text-yellow-900">Poids (kg)</label>
          <div className="flex gap-2">
            <input
              type="number"
              step="0.001"
              min="0"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
              placeholder="0.000"
              className="flex-1 px-3 py-3 text-base border-2 border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[48px]"
            />
            <button
              onClick={handleManualSubmit}
              disabled={!manualInput}
              className="px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-40 font-semibold text-base min-h-[48px]"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Section 4: Bouton connexion/déconnexion */}
      <div className="w-32 flex flex-col justify-center">
        {!connected ? (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="h-full px-3 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-base transition-colors flex flex-col items-center justify-center gap-1.5"
          >
            <span className="text-3xl">🔌</span>
            <span>{connecting ? 'Connexion...' : 'Connecter'}</span>
            <span className="text-xs opacity-75">{adapterName}</span>
          </button>
        ) : (
          <button
            onClick={handleDisconnect}
            className="h-full px-3 py-3 border-2 border-red-400 text-red-600 rounded-xl hover:bg-red-50 active:bg-red-100 font-semibold text-base transition-colors flex flex-col items-center justify-center gap-1.5"
          >
            <span className="text-2xl">✖️</span>
            <span>Déconnecter</span>
          </button>
        )}
      </div>
    </div>
  );
}
